import { IconX } from "@tabler/icons-react";
import RaidoField from "../../../components/common/RaidoField";
import { useState } from "react";
import InputField from "../../../components/common/InputField";
import { usePermission } from "../../../hooks/usePermissionTabs";

type Props = {
  invoice: any;
  onClose: () => void;
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between items-center w-full">
    <div className="w-full">
      <h2 className="capitalize text-[#212121] text-xs font-semibold">{label}</h2>
    </div>
    <div className="w-full">
      <p className="text-[#1E293B] text-xs font-medium">{value}</p>
    </div>
  </div>
);

const ViewModal = ({ invoice, onClose }: Props) => {
  const { allowed: canUpdateInvoice } = usePermission("invoices", "update");
  const [mode, setMode] = useState("credit");

  if (!invoice) return null;
  
  return (
    <div className="relative border rounded-lg border-gray-200 flex flex-col overflow-hidden w-full z-10">
      {/* Sidebar Header */}
      <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-[#212121]">View Invoice – {invoice.invoiceId}</h2>

        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#D2D2D2] hover:bg-slate-50 cursor-pointer bg-white text-slate-700"
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      {/* Sidebar Content */}
      <div className="p-6 overflow-y-auto max-h-[calc(100vh-220px)] space-y-4">
        <InfoRow label="Invoice ID" value={invoice.invoiceId} />
        <InfoRow label="Amount Due" value={invoice.amount} />
        <InfoRow label="Due Date" value={invoice.dueDate} />
        <InfoRow label="Description" value={invoice.description} />
        
        <div className="flex justify-between items-center w-full pt-3">
          <div className="w-full">
            <h2 className="text-xs text-[#212121] font-semibold">Need To Pay</h2>
          </div>
          <div className="w-full">
            <p className="text-sm text-[#212121] font-semibold">{invoice.amount}</p>
          </div>
        </div>

        <div className="pt-3">
          <RaidoField
            label="Payment Method"
            value={mode}
            onClick={(val) => {
              if (typeof val === "string") {
                setMode(val);
              }
            }}
            valuesArr={[
              { value: "credit", text: "Credit/Debit Card" },
              { value: "upi", text: "UPI / Paytm" },
              { value: "bank", text: "Bank Transfer" },
            ]}
          />
        </div>

        {mode === "credit" && (
          <div className="grid grid-cols-2 gap-4 pt-2">
            <InputField
              type="text"
              className="flex items-center col-span-2 text-xs"
              inputClassName="bg-yellow-50"
              label="Card Number"
              placeholder="985632147036522"
            />
            <InputField
              type="date"
              className="flex items-center text-xs"
              inputClassName="bg-yellow-50"
              label="Expiry Date"
              placeholder={""}
            />
            <InputField
              type="text"
              className="flex items-center text-xs"
              inputClassName="bg-yellow-50"
              label="CVV"
              placeholder="410"
            />
          </div>
        )}
        {mode === "upi" && (
          <div className="grid grid-cols-2 gap-4 pt-2">
            <InputField
              type="text"
              className="flex items-center col-span-2 text-xs"
              inputClassName="bg-yellow-50"
              label="Enter UPI ID"
              placeholder="985632147036522"
            />
          </div>
        )}
        {mode === "bank" && (
          <div className="grid grid-cols-2 gap-4 pt-2">
            <InputField
              type="text"
              className="flex items-center col-span-2 text-xs"
              inputClassName="bg-yellow-50"
              label="UTR/Reference No."
              placeholder="985632147036522"
            />
            <InputField
              type="text"
              className="flex items-center col-span-2 text-xs"
              inputClassName="bg-yellow-50"
              label="Billing Email (optional)"
              placeholder=" your@email.com"
            />
          </div>
        )}
      </div>

      {/* Pay Action Footer */}
      {canUpdateInvoice && (
        <div className="px-6 py-4 flex justify-center">
          <button
            className="bg-[#FBD037] hover:bg-[#e2bd30] w-full rounded-full p-2 px-6 text-[#131327] text-base font-semibold transition-all duration-150 active:scale-95 shadow-sm hover:shadow"
            onClick={onClose}
          >
            Pay {invoice.amount}
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewModal;
